import {
  ActionCreator, EmptyActionCreator, ActionCreatorFactory,
} from "typescript-fsa";
import {Component, PropTypes, createElement} from "react";
import {Action, Dispatch} from "redux";
import {RouteComponentProps} from "react-router";
import {SagaIterator, Channel, buffers} from "redux-saga";
import {take, spawn, cancel, actionChannel} from "redux-saga/effects";
import {isEqual} from "lodash";

import {WrappedComponent} from "./components";


export interface RouteActions<P> {
  entered: EmptyActionCreator;
  paramsChanged: ActionCreator<P>;
  leaved: EmptyActionCreator;
}

export function routeActions<P>(
  actionCreator: ActionCreatorFactory,
): RouteActions<P> {
  return {
    entered: actionCreator(`ENTERED`),
    paramsChanged: actionCreator<P>(`PARAMS_CHANGED`),
    leaved: actionCreator(`LEAVED`),
  };
}

export function routeSaga<P>(routeActions: RouteActions<P>) {
  return (saga: (paramsChangedChannel: Channel<P>) => SagaIterator) =>
    function* routeSaga(): SagaIterator {
      const paramsChangedChannel = yield actionChannel(
        routeActions.paramsChanged.type,
        buffers.sliding<Action>(1),
      );

      while (true) {
        yield take(routeActions.entered.type);

        const task = yield spawn(saga, paramsChangedChannel);

        yield take(routeActions.leaved.type);

        yield cancel(task);
      }
    };
}

export type InjectedProps<P, Params> = RouteComponentProps<Params, Params> & P;

export function routeComponent<P, Params>(actions: RouteActions<Params>) {
  type Props = InjectedProps<P, Params>;

  return (component: WrappedComponent<Props>) => {
    return class RouteComponent extends Component<Props, {
      initialized: boolean;
    }> {
      state = {
        initialized: false,
      };

      static contextTypes = {
        store: PropTypes.object,
      };

      private getDispatch() {
        return this.context.store.dispatch as Dispatch<any>;
      }

      componentDidMount() {
        const dispatch = this.getDispatch();
        dispatch(actions.entered());
        dispatch(actions.paramsChanged(this.props.routeParams!));

        this.setState({
          initialized: true,
        });
      }

      componentDidUpdate(prevProps: RouteComponentProps<Params, Params>) {
        if (!isEqual(prevProps.routeParams, this.props.routeParams))
          this.getDispatch()(actions.paramsChanged(this.props.routeParams!));
      }

      componentWillUnmount() {
        this.getDispatch()(actions.leaved());
        this.setState({
          initialized: false,
        });
      }

      render() {
        return !this.state.initialized ? null :
               createElement(component as any, this.props);
      }
    };
  };
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/utils/router.ts